import { css, html, LitElement, nothing } from 'lit';

import '@brightspace-ui/core/components/button/button.js';
import '@brightspace-ui/core/components/inputs/input-text.js';
import '@brightspace-ui/core/components/collapsible-panel/collapsible-panel.js';

class PMTenantLabels extends LitElement {

  static get properties() {
    return {
      environmentId: { type: String, attribute: 'environment-id' },
      tenantId: { type: String, attribute: 'tenant-id' },
      client: { type: Object },
      labels: { type: Array },
      readonly: { type: Boolean },
      noPanel: { type: Boolean, attribute: 'no-panel' },
      _newLabel: { type: String, state: true },
    };
  }

  static get styles() {
    return css`
      :host {
        display: block;
      }
      .labels-container {
        display: flex;
        gap: 6px;
        flex-wrap: wrap;
        margin-bottom: 8px;
      }
      .label-pill {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        background: rgba(6, 182, 212, 0.1);
        color: #0891b2;
        border: 1px solid rgba(6, 182, 212, 0.25);
        border-radius: 999px;
        padding: 4px 8px;
        font-size: 12px;
        font-weight: 600;
      }
      .label-pill.display-only {
        padding: 4px 10px;
      }
      .remove-btn {
        appearance: none;
        border: none;
        background: transparent;
        cursor: pointer;
        color: #0891b2;
        padding: 0;
        font-size: 14px;
        line-height: 1;
      }
      .add-row {
        display: flex;
        gap: 8px;
        align-items: center;
      }
      .add-row d2l-input-text {
        flex: 1;
      }
    `;
  }

  constructor() {
    super();
    this.labels = [];
    this.readonly = false;
    this.noPanel = false;
    this._newLabel = '';
  }

  _handleNewLabelInput(e) {
    this._newLabel = e.target.value;
  }

  async _addLabel() {
    const label = this._newLabel.trim();
    if (!label) return;
    const previousLabels = [...this.labels];
    this.labels = [...this.labels, label];
    this._newLabel = '';
    this._dispatchChange();
    try {
      await this.client.updateTenantLabels(this.environmentId, this.tenantId, this.labels);
    } catch (err) {
      this.labels = previousLabels;
      this._dispatchChange();
      this._createAlert('error', `Failed to add label: ${err.message}`);
    }
  }

  _handleLabelKeydown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      this._addLabel();
    }
  }

  async _handleRemoveLabel(e) {
    const index = parseInt(e.currentTarget.dataset.index, 10);
    const previousLabels = [...this.labels];
    this.labels = this.labels.filter((_, i) => i !== index);
    this._dispatchChange();
    try {
      await this.client.updateTenantLabels(this.environmentId, this.tenantId, this.labels);
    } catch (err) {
      this.labels = previousLabels;
      this._dispatchChange();
      this._createAlert('error', `Failed to remove label: ${err.message}`);
    }
  }

  _dispatchChange() {
    this.dispatchEvent(new CustomEvent('labels-changed', {
      detail: { labels: this.labels },
      bubbles: true,
      composed: true,
    }));
  }

  _createAlert(type, message) {
    document.dispatchEvent(new CustomEvent('kraken-alert-event', {
      detail: { message, type },
      bubbles: true,
      composed: true,
    }));
  }

  _renderLabelsOnly() {
    if (!this.labels?.length) return nothing;
    return html`
      <div class="labels-container">
        ${this.labels.map(label => html`
          <span class="label-pill display-only">${label}</span>
        `)}
      </div>
    `;
  }

  _renderEditableContent() {
    return html`
      <div class="labels-container">
        ${this.labels.map((label, index) => html`
          <span class="label-pill">
            ${label}
            <button class="remove-btn" data-index="${index}" @click="${this._handleRemoveLabel}">×</button>
          </span>
        `)}
      </div>
      <div class="add-row">
        <d2l-input-text label="New label" label-hidden placeholder="Add a label..." .value="${this._newLabel}" @change="${this._handleNewLabelInput}" @keydown="${this._handleLabelKeydown}"></d2l-input-text>
        <d2l-button @click="${this._addLabel}">Add</d2l-button>
      </div>
    `;
  }

  render() {
    if (this.readonly) {
      return this._renderLabelsOnly();
    }

    if (this.noPanel) {
      return html`
        <d2l-collapsible-panel panel-title="Tenant Labels" expanded style="margin-bottom: 18px;">
          ${this._renderEditableContent()}
        </d2l-collapsible-panel>
      `;
    }

    return html`
      <d2l-collapsible-panel panel-title="Tenant Labels">
        ${this._renderEditableContent()}
      </d2l-collapsible-panel>
    `;
  }
}

window.customElements.define('pm-tenant-labels', PMTenantLabels);
